import type { RankDefinition } from "@quizshow/protocol";
import { currentRank } from "./game-status";
import { RoomImage } from "./media";

export interface RankLadderEntry {
  id: string;
  label: string;
  progress: number;
  color?: string;
}

export function RankLadder({
  ranks,
  entries,
  primaryEntryId,
  unitLabel = "Fortschritt",
  roomId,
  token,
  compact = false,
}: {
  ranks: RankDefinition[];
  entries: RankLadderEntry[];
  primaryEntryId?: string;
  unitLabel?: string;
  roomId?: string;
  token?: string;
  compact?: boolean;
}) {
  if (!ranks.length) return null;
  const ordered = [...ranks].sort(
    (left, right) => left.requiredProgress - right.requiredProgress || left.name.localeCompare(right.name),
  );
  const primary = entries.find((entry) => entry.id === primaryEntryId) ?? entries[0];
  const primaryRank = primary ? currentRank(ordered, primary.progress) : undefined;
  const nextRank = primary
    ? ordered.find((rank) => rank.requiredProgress > primary.progress)
    : undefined;

  return (
    <aside className={`rank-ladder ${compact ? "rank-ladder--compact" : ""}`} aria-label="Rangstufen">
      <div className="rank-ladder__heading">
        <span className="eyebrow">Rangstufen</span>
        {primary ? <small>{primary.label}: {primary.progress} {unitLabel}</small> : null}
      </div>
      <div className="rank-ladder__list">
        {ordered.map((rank) => {
          const occupants = entries.filter((entry) => currentRank(ordered, entry.progress)?.rankId === rank.rankId);
          const isCurrent = primaryRank?.rankId === rank.rankId;
          const isNext = nextRank?.rankId === rank.rankId;
          return (
            <div
              className={`rank-ladder__step ${isCurrent ? "rank-ladder__step--current" : ""} ${
                isNext ? "rank-ladder__step--next" : ""
              }`}
              key={rank.rankId}
              style={{ "--rank-color": rank.color, "--rank-background": rank.backgroundColor } as React.CSSProperties}
            >
              {rank.imageMediaId ? (
                <span className="rank-ladder__icon rank-ladder__icon--image" data-media-id={rank.imageMediaId}>
                  <span>{rank.icon || rank.name.slice(0, 1)}</span>
                  <RoomImage
                    roomId={roomId}
                    token={token}
                    mediaId={rank.imageMediaId}
                    className="rank-ladder__icon-image"
                  />
                </span>
              ) : (
                <span className="rank-ladder__icon rank-ladder__icon--fallback">
                  {rank.icon || rank.name.slice(0, 1)}
                </span>
              )}
              <span className="rank-ladder__name">
                <strong>{rank.name}</strong>
                <small>ab {rank.requiredProgress} {unitLabel}</small>
              </span>
              {isCurrent ? <span className="rank-ladder__state">Aktuell</span> : null}
              {isNext ? <span className="rank-ladder__state">Nächster</span> : null}
              {occupants.length ? (
                <span className="rank-ladder__occupants">
                  {occupants.map((entry) => (
                    <small key={entry.id} style={{ color: entry.color }}>
                      {entry.label}
                    </small>
                  ))}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
